import { ComponentProps, FC, Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as _ from "lodash";
import { withProps } from "hoc-with-props";
import * as Sentry from "@sentry/react";

import {
  ChartContainerList,
  ChartContainerReference,
  DataDescriptorEntity,
  Enterprise as App,
  EnterpriseAppState,
  FarmEntity,
  LayoutType,
  SectionEntity,
  SectionList,
  SectionReference,
  ISODuration
} from "@ctra/api";

import { Analytics, Charts as ChartUI, Col, Collapse, CtraLayout, Row } from "@ctra/components";
import { defaultLocale, Enterprise, useTranslation } from "@ctra/i18n";
import { ChartLegendValuesContext } from "@ctra/charts";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Nullable } from "@ctra/utils";

import { Chart, ChartFilterContext, GAActions as ChartGAActions, ChartAPIContext } from "@chart";
import { GAActions, SectionContext, useSection } from "@analytics";
import { Routes } from "@routes";

import { SectionToolbar } from "../../shared/SectionToolbar";
import { CustomPanel } from "./components";
import styles from "./Recursion.module.less";

const { SectionHeader } = Analytics;
const { ContentWrapper } = CtraLayout;
const { ChartVariant } = ChartUI;

interface RecursionProps {
  sectionID: SectionEntity["id"];
  farmID?: FarmEntity["id"];
  metadata?: {
    rootSectionID?: SectionEntity["id"];
  };
}

type Reference = ChartContainerReference | SectionReference;

interface Group {
  schema: LayoutType;
  children: Array<Reference>;
}

/**
 * Partition the given reference array of the section to group chart containers and sections together
 * @example [[chart, chart], [section], [chart], [section, section, section]]
 */
const partition = (children: Reference[]) =>
  children.reduce((res: Array<Array<Reference>>, reference: Reference, idx: number) => {
    const { schema } = reference;
    const { schema: prev } = _.defaultTo(children[idx - 1], {} as Reference);

    if (prev && prev === schema) {
      const source = _.defaultTo(_.last(res), []);

      res.splice(res.length - 1, 1, _.concat(source, reference));
    } else {
      res.push([reference]);
    }

    return res;
  }, []);

/**
 * Recursive section/chart structure
 * @param {number | undefined} farmID
 * @param {string} sectionID
 * @param {{rootSectionID?: SectionEntity["id"]} | undefined} metadata
 * @returns {JSX.Element}
 * @constructor
 */
export const Recursion: FC<RecursionProps> = ({ farmID, sectionID, metadata }) => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { section } = useSection();

  /**
   * Keep a history of the visited subsections so if the user returns to a
   * place which was previously visited, we don't force-open the first panel
   */
  const [sectionHistory, setSectionHistory] = useState<SectionEntity["id"][]>([]);

  /**
   * Keep a state and make the Collapse a managed component
   */
  const [openPanels, setOpenPanels] = useState<SectionEntity["id"][]>([]);

  /**
   * Keep a state to show/hide projections
   */
  const [showProjections, setShowProjections] = useState<boolean>(false);

  const { rootSectionID } = _.defaultTo(metadata, {});
  const search = new URLSearchParams();

  const {
    chart: { title: chartTitle },
    layouts: {
      section: { title: layoutTitle }
    }
  } = Enterprise;

  if (farmID) {
    search.append("farmID", farmID.toString());
  }

  /**
   * Get the section entity from the state
   */
  const { children } = useSelector<EnterpriseAppState, SectionEntity>(
    (state) => App.entities.getSectionsByID(state, { sectionIDs: [sectionID] })[sectionID]
  );

  /**
   * Make the first panel open by default unless the user
   * has already visited the section before (to avoid flickering)
   */
  useEffect(() => {
    if (!sectionHistory.includes(sectionID)) {
      setOpenPanels(_.uniq([...openPanels, _.get(children, "0.id")]));
      setSectionHistory(_.uniq([...sectionHistory, sectionID]));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionID]);

  /**
   * Get a list of sections for the given layout
   * so the section names may be resolved
   * @type {SectionList}
   */
  const sectionList = useSelector<EnterpriseAppState, SectionList>((state) =>
    App.entities.getSections(state)
  );

  /**
   * Get a list of chart containers for the section
   * @todo get only for this section, flat
   * @type {ChartContainerList}
   */
  const chartContainers = useSelector<EnterpriseAppState, ChartContainerList>((state) =>
    App.entities.getChartContainersOfSection(state, { sectionID })
  );

  /**
   * Get a map of sections to unsupported data descriptor id's
   * @type {Record<SectionEntity["id"], Array<DataDescriptorEntity["id"]>>}
   */
  const unsupportedChartsMap = useSelector<
    EnterpriseAppState,
    Record<SectionEntity["id"], Array<DataDescriptorEntity["id"]>>
  >((state) => (_.isEmpty(children) ? {} : App.entities.getSupportedCharts(state, { farmID, invert: true })));

  /**
   * Partition the children and append the schema to the data
   * so we can tell which component to render for which group
   * @type {Group[]}
   */
  const withSchema: Array<Group> = _.map(partition(children), (part) => {
    const [{ schema }] = part;

    return {
      schema,
      children: part
    };
  });

  /**
   * Handle comparing charts
   * @param {ChartContainerReference["id"]} id
   * @param {SectionEntity["id"]} rootSectionID
   * @param {Nullable<ISODuration>} isoDuration
   */
  const handleCompare = (
    id: ChartContainerReference["id"],
    rootSectionID?: SectionEntity["id"],
    isoDuration?: Nullable<ISODuration>
  ) => {
    if (rootSectionID) {
      search.append("dataDescriptorID", chartContainers[id].dataDescriptorID);
    }

    if (isoDuration) {
      search.append("isoDuration", isoDuration);
    }

    App.history.push({
      pathname: Routes.app.analytics.compareCharts.index,
      search: search.toString()
    });
  };

  /**
   * Handle collapsing and opening sections
   * @param {string | string[]} panels
   */
  const handleCollapse: ComponentProps<typeof Collapse>["onChange"] = (panels) => {
    if (_.isArray(panels)) {
      /**
       * The last panel in the symmetric difference array between the current panel list and the new list
       * is the panel the user just interacted with.
       */
      const symmetricDifference = _.xor(panels, openPanels);
      const actionPanel = _.last(symmetricDifference) || _.get(children, "0.id");

      const action = _.includes(openPanels, actionPanel)
        ? GAActions.closeSubsection
        : GAActions.openSubsection;

      setOpenPanels(panels);

      /* istanbul ignore next */
      trackEvent(action, { label: sectionList[actionPanel]?.title });
    }
  };

  /**
   * Maintain an array of unsupported charts
   * @type {any[]}
   */
  const unsupportedCharts: Array<DataDescriptorEntity["id"]> = [];

  /**
   * Don't use a second section provider for the chart list if there is already one
   * @type {import("react").ForwardRefExoticComponent<object & import("react").RefAttributes<unknown>>}
   */
  const SectionProviderWrapper = useMemo(
    () => (section === null ? withProps({ sectionID, rootSectionID })(SectionContext.Provider) : Fragment),
    [sectionID, rootSectionID, section]
  );

  /**
   * Don't use a second chart filter provider for the chart list if there is already one
   * @type {React.FunctionComponent<FilterProviderProps> | React.ExoticComponent<{children?: React.ReactNode | undefined}>}
   */
  const ChartFilterProviderWrapper = useMemo(
    () => (section === null ? ChartFilterContext.Provider : Fragment),
    [section]
  );

  return (
    <>
      {_.flatMap(withSchema, ({ schema, children }) =>
        schema === LayoutType.chartContainer ? (
          <ChartFilterProviderWrapper>
            <SectionProviderWrapper>
              {sectionID === rootSectionID && (
                <SectionToolbar
                  sectionID={sectionID}
                  projections={{
                    state: showProjections,
                    onChange: setShowProjections
                  }}
                  className={styles.Toolbar}
                  justify="end"
                />
              )}
              <Row className={styles.Stack} gutter={[16, 16]}>
                {children.map(({ id }) => {
                  /**
                   * Get the DD id of chart from chart containers
                   */
                  const dataDescriptorID = chartContainers[id].dataDescriptorID;

                  /**
                   * Check whether chart has support
                   */
                  const isUnsupported = _.includes(unsupportedChartsMap[sectionID], dataDescriptorID);

                  /**
                   * Keep track of unsupported charts by pushing it in an array
                   */
                  if (isUnsupported) {
                    unsupportedCharts.push(dataDescriptorID);
                  }

                  /**
                   * Do not show the chart without support
                   */
                  return (
                    !isUnsupported && (
                      <Sentry.ErrorBoundary fallback={<>{null}</>}>
                        <ChartAPIContext.Provider
                          dataDescriptorID={dataDescriptorID}
                          farmID={farmID}
                          flags={{ showProjections }}
                        >
                          <ChartLegendValuesContext.Provider>
                            <ChartAPIContext.Consumer>
                              {({ chart: { title } }) => (
                                <Col key={id} lg={12} span={24}>
                                  <ChartFilterContext.Consumer>
                                    {({ filters, isoDuration, timePeriod }) => (
                                      <ContentWrapper
                                        interactive
                                        onClick={() => {
                                          trackEvent(ChartGAActions.expandChart, {
                                            label: t<string>(chartTitle(title), { lng: defaultLocale })
                                          });

                                          App.history.push({
                                            state: {
                                              zoom: {
                                                rootSectionID,
                                                dataDescriptorID,
                                                filters,
                                                isoDuration,
                                                timePeriod
                                              },
                                              timeline: {
                                                disabled: true
                                              }
                                            }
                                          });
                                        }}
                                        padded={false}
                                      >
                                        <Chart
                                          variant={ChartVariant.V2}
                                          handleCompare={() => handleCompare(id, rootSectionID, isoDuration)}
                                          viewOptions={{
                                            showFavouriteIcon: true,
                                            showSupportedViews: true
                                          }}
                                        />
                                      </ContentWrapper>
                                    )}
                                  </ChartFilterContext.Consumer>
                                </Col>
                              )}
                            </ChartAPIContext.Consumer>
                          </ChartLegendValuesContext.Provider>
                        </ChartAPIContext.Provider>
                      </Sentry.ErrorBoundary>
                    )
                  );
                })}
              </Row>
            </SectionProviderWrapper>
          </ChartFilterProviderWrapper>
        ) : (
          <Collapse
            bordered={false}
            className={styles.Collapse}
            activeKey={openPanels}
            onChange={handleCollapse}
          >
            {children.map(({ id }) => (
              <CustomPanel
                sectionID={id}
                showArrow={false}
                header={
                  <SectionHeader
                    title={t<string>(layoutTitle(sectionList[id].title), { makeDefaultValue: true })}
                    toolbar={
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <SectionToolbar
                          projections={{
                            state: showProjections,
                            onChange: setShowProjections
                          }}
                          sectionID={sectionID}
                          disabled={!openPanels.includes(id)}
                        />
                      </div>
                    }
                  />
                }
                key={id}
              >
                <Recursion farmID={farmID} sectionID={id} metadata={metadata} />
              </CustomPanel>
            ))}
          </Collapse>
        )
      )}
    </>
  );
};
